import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  adminOrgs,
  type OrgRow,
  type OrgCreate,
  type OrgUserCreate,
} from "@/api/adminPanel";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Tenant provisioning — super-admin only.
 *
 * Lists every org on the platform, supports create / edit / disable, and
 * a "Bootstrap user" affordance per row that drops in the first user
 * for a freshly-provisioned tenant. The new user lands with a hashed
 * password they can use immediately.
 */
export function AdminOrganizationsPage() {
  const list = useQuery({ queryKey: ["admin-orgs"], queryFn: () => adminOrgs.list() });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<OrgRow | null>(null);
  const [bootstrapping, setBootstrapping] = useState<OrgRow | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const disable = useMutation({
    mutationFn: (id: string) => adminOrgs.disable(id),
    onSuccess: () => {
      toast.push("Tenant disabled", "warn");
      qc.invalidateQueries({ queryKey: ["admin-orgs"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Tenants</h1>
          <p className="text-2xs text-term-mute">
            Each row is one whitelabel broker hosted on the platform.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>New tenant</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : list.data?.length === 0 ? (
            <EmptyState
              title="No tenants yet"
              description="Create the first tenant — pick a slug, give it a name, and bootstrap an admin user."
              action={<Button onClick={() => setCreateOpen(true)}>Create first tenant</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Slug</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Name</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Plan</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Region</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Domain</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((o) => (
                  <tr key={o.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{o.slug}</td>
                    <td className="px-3 py-1.5 text-term-fg">{o.name}</td>
                    <td className="px-3 py-1.5 text-term-dim">{o.plan}</td>
                    <td className="px-3 py-1.5 text-term-dim">{o.region}</td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {o.primary_domain ?? "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge tone={o.status === "active" ? "good" : o.status === "disabled" ? "bad" : "warn"}>
                        {o.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setBootstrapping(o)}>
                          Bootstrap user
                        </Button>
                        <Button variant="ghost" onClick={() => setEditing(o)}>
                          Edit
                        </Button>
                        {o.status !== "disabled" && (
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Disable tenant ${o.slug}?`)) disable.mutate(o.id);
                            }}
                          >
                            Disable
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {createOpen && <CreateOrgModal onClose={() => setCreateOpen(false)} />}
      {editing && <EditOrgModal org={editing} onClose={() => setEditing(null)} />}
      {bootstrapping && (
        <BootstrapUserModal org={bootstrapping} onClose={() => setBootstrapping(null)} />
      )}
    </div>
  );
}


function CreateOrgModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<OrgCreate>({
    slug: "",
    name: "",
    plan: "standard",
    region: "local",
    status: "active",
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => adminOrgs.create(form),
    onSuccess: (r) => {
      toast.push(`Tenant ${r.slug} created`, "success");
      qc.invalidateQueries({ queryKey: ["admin-orgs"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title="New tenant">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Slug</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
              placeholder="acme"
              required
            />
          </div>
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Acme Brokers Ltd"
              required
            />
          </div>
          <div>
            <Label>Plan</Label>
            <Select value={form.plan ?? "standard"} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              <option value="standard">standard</option>
              <option value="pro">pro</option>
              <option value="enterprise">enterprise</option>
            </Select>
          </div>
          <div>
            <Label>Region</Label>
            <Select value={form.region ?? "local"} onChange={(e) => setForm({ ...form, region: e.target.value })}>
              <option value="local">local</option>
              <option value="us-east">us-east</option>
              <option value="eu-west">eu-west</option>
              <option value="ap-south">ap-south</option>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Primary domain (optional)</Label>
            <Input
              value={form.primary_domain ?? ""}
              onChange={(e) => setForm({ ...form, primary_domain: e.target.value || undefined })}
              placeholder="trade.acme.com"
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending}>Create</Button>
        </div>
      </form>
    </Modal>
  );
}


function EditOrgModal({ org, onClose }: { org: OrgRow; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({
    name: org.name,
    plan: org.plan,
    region: org.region,
    status: org.status as "active" | "disabled" | "suspended",
    primary_domain: org.primary_domain ?? "",
    licensed_entity_name: org.licensed_entity_name ?? "",
    jurisdiction: org.jurisdiction ?? "",
    regulatory_authority: org.regulatory_authority ?? "",
    license_number: org.license_number ?? "",
    compensation_scheme: (org.compensation_scheme ?? "") as
      | ""
      | "FSCS"
      | "ICF"
      | "SIPC"
      | "EDB"
      | "NONE",
    complaints_email: org.complaints_email ?? "",
    terms_url: org.terms_url ?? "",
    privacy_url: org.privacy_url ?? "",
    risk_warning_html: org.risk_warning_html ?? "",
  });
  const [err, setErr] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: () =>
      adminOrgs.patch(org.id, {
        name: form.name,
        plan: form.plan,
        region: form.region,
        status: form.status,
        primary_domain: form.primary_domain || undefined,
        licensed_entity_name: form.licensed_entity_name || undefined,
        jurisdiction: form.jurisdiction || undefined,
        regulatory_authority: form.regulatory_authority || undefined,
        license_number: form.license_number || undefined,
        compensation_scheme: form.compensation_scheme || undefined,
        complaints_email: form.complaints_email || undefined,
        terms_url: form.terms_url || undefined,
        privacy_url: form.privacy_url || undefined,
        risk_warning_html: form.risk_warning_html || undefined,
      }),
    onSuccess: () => {
      toast.push("Tenant updated", "success");
      qc.invalidateQueries({ queryKey: ["admin-orgs"] });
      qc.invalidateQueries({ queryKey: ["branding"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={`Edit ${org.slug}`} size="lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          patch.mutate();
        }}
        className="space-y-4"
      >
        {/* Basic tenant settings */}
        <fieldset className="space-y-3 rounded border border-term-border p-3">
          <legend className="px-1 text-2xs uppercase tracking-wider text-term-mute">
            Tenant
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Plan</Label>
              <Select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                <option value="standard">standard</option>
                <option value="pro">pro</option>
                <option value="enterprise">enterprise</option>
              </Select>
            </div>
            <div>
              <Label>Region</Label>
              <Select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
                <option value="local">local</option>
                <option value="us-east">us-east</option>
                <option value="eu-west">eu-west</option>
                <option value="ap-south">ap-south</option>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as typeof form.status })}
              >
                <option value="active">active</option>
                <option value="suspended">suspended</option>
                <option value="disabled">disabled</option>
              </Select>
            </div>
            <div>
              <Label>Primary domain</Label>
              <Input
                value={form.primary_domain}
                onChange={(e) => setForm({ ...form, primary_domain: e.target.value })}
                placeholder="trade.acme.com"
              />
            </div>
          </div>
        </fieldset>

        {/* Compliance attribution — Phase 10A.1 */}
        <fieldset className="space-y-3 rounded border border-term-border p-3">
          <legend className="px-1 text-2xs uppercase tracking-wider text-term-mute">
            Licensed entity (compliance)
          </legend>
          <p className="text-2xs text-term-dim">
            Filled in by the platform operator before flipping the tenant
            to live trading. Drives the global UI footer (entity, license,
            compensation scheme, risk warning) and complaint-routing email.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Licensed entity name</Label>
              <Input
                value={form.licensed_entity_name}
                onChange={(e) =>
                  setForm({ ...form, licensed_entity_name: e.target.value })
                }
                placeholder="Acme Brokers Cyprus Ltd"
              />
            </div>
            <div>
              <Label>Jurisdiction (ISO 3166-1 alpha-2/3)</Label>
              <Input
                value={form.jurisdiction}
                onChange={(e) =>
                  setForm({ ...form, jurisdiction: e.target.value.toUpperCase() })
                }
                placeholder="CY"
                maxLength={8}
              />
            </div>
            <div>
              <Label>Regulatory authority</Label>
              <Input
                value={form.regulatory_authority}
                onChange={(e) =>
                  setForm({ ...form, regulatory_authority: e.target.value })
                }
                placeholder="CySEC"
              />
            </div>
            <div>
              <Label>License number</Label>
              <Input
                value={form.license_number}
                onChange={(e) =>
                  setForm({ ...form, license_number: e.target.value })
                }
                placeholder="123/45"
              />
            </div>
            <div>
              <Label>Compensation scheme</Label>
              <Select
                value={form.compensation_scheme}
                onChange={(e) =>
                  setForm({
                    ...form,
                    compensation_scheme: e.target.value as typeof form.compensation_scheme,
                  })
                }
              >
                <option value="">— none —</option>
                <option value="ICF">ICF (Cyprus, up to €20k)</option>
                <option value="FSCS">FSCS (UK, up to £85k)</option>
                <option value="SIPC">SIPC (US, up to $500k)</option>
                <option value="EDB">EDB (DE, up to €100k)</option>
                <option value="NONE">NONE (unregulated)</option>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Complaints email</Label>
              <Input
                type="email"
                value={form.complaints_email}
                onChange={(e) =>
                  setForm({ ...form, complaints_email: e.target.value })
                }
                placeholder="complaints@acme.com"
              />
            </div>
            <div>
              <Label>Terms URL</Label>
              <Input
                value={form.terms_url}
                onChange={(e) => setForm({ ...form, terms_url: e.target.value })}
                placeholder="https://acme.com/terms"
              />
            </div>
            <div>
              <Label>Privacy URL</Label>
              <Input
                value={form.privacy_url}
                onChange={(e) => setForm({ ...form, privacy_url: e.target.value })}
                placeholder="https://acme.com/privacy"
              />
            </div>
            <div className="col-span-2">
              <Label>Risk warning HTML</Label>
              <textarea
                className="block h-24 w-full rounded border border-slate-300 dark:border-slate-700 bg-transparent p-2 font-mono text-2xs"
                value={form.risk_warning_html}
                onChange={(e) =>
                  setForm({ ...form, risk_warning_html: e.target.value })
                }
                placeholder="<strong>76% of retail accounts lose money trading CFDs with this provider.</strong> You should consider whether you can afford to take the high risk of losing your money."
              />
              <p className="mt-1 text-2xs text-term-dim">
                Rendered verbatim in the global footer. Sanitize on input —
                this is a trusted-admin field.
              </p>
            </div>
          </div>
        </fieldset>

        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={patch.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}


function BootstrapUserModal({ org, onClose }: { org: OrgRow; onClose: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState<OrgUserCreate>({
    email: "",
    password: "",
    full_name: "",
    is_superuser: false,
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => adminOrgs.bootstrapUser(org.id, form),
    onSuccess: (u) => {
      toast.push(`User ${u.email} created in ${org.slug}`, "success");
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={`Bootstrap user · ${org.slug}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <p className="text-2xs text-term-dim">
          Creates the first (or any) user in this tenant. Use this to seed
          an org_admin so they can self-onboard the rest of their team via
          the normal /auth/register flow.
        </p>
        <div>
          <Label>Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </div>
        <div>
          <Label>Password (min 10 chars)</Label>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            minLength={10}
            required
          />
        </div>
        <div>
          <Label>Full name (optional)</Label>
          <Input
            value={form.full_name ?? ""}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="is_su"
            type="checkbox"
            checked={!!form.is_superuser}
            onChange={(e) => setForm({ ...form, is_superuser: e.target.checked })}
          />
          <label htmlFor="is_su" className="text-2xs uppercase tracking-wider text-term-dim">
            Mark as super_admin (platform operator)
          </label>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending}>Create user</Button>
        </div>
      </form>
    </Modal>
  );
}
